import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@ui/checkbox.tsx';
import { Button } from '@ui/button.tsx';
import { cn } from '@r/utils/utils.ts';
import type { ProductBrandProductBrandReadModel } from '@r/api/generated/client.schemas.ts';

/** Nombre de marques affichées avant le bouton « Afficher plus ». */
const INITIAL_BRANDS_DISPLAY_COUNT = 10;

type ProductBrandFiltersProps = {
  brands: ProductBrandProductBrandReadModel[];
  selectedBrandIds: number[];
  onToggleBrand: (brandId: number) => void;
};

/**
 * Affiche la liste des marques filtrables du catalogue avec cases à cocher
 * Limite l'affichage initial à 10 marques avec un bouton « Afficher plus »
 */
const ProductBrandFilters = ({
  brands,
  selectedBrandIds,
  onToggleBrand
}: ProductBrandFiltersProps) => {
  const { t } = useTranslation();
  const brandFilterId = useId();
  const [showAllBrands, setShowAllBrands] = useState(false);

  useEffect(() => {
    setShowAllBrands(false);
  }, [brands]);

  if (brands.length === 0) return null;

  const visibleBrands = showAllBrands ? brands : brands.slice(0, INITIAL_BRANDS_DISPLAY_COUNT);
  const hasMoreBrands = brands.length > INITIAL_BRANDS_DISPLAY_COUNT;

  return (
    <div className="pt-4 space-y-2 border-t">
      <h3 className="text-sm font-semibold">{t('shop.filters.brands')}</h3>

      <div className="space-y-1">
        {visibleBrands.map((brand) => {
          const isSelected = selectedBrandIds.includes(brand.id);
          const checkboxId = `${brandFilterId}-brand-filter-${brand.id}`;

          return (
            <label
              key={brand.id}
              htmlFor={checkboxId}
              className={cn(
                'flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-colors hover:bg-muted/30',
                isSelected && 'bg-primary/10'
              )}
            >
              <Checkbox
                id={checkboxId}
                checked={isSelected}
                onCheckedChange={() => onToggleBrand(brand.id)}
              />
              <span
                className={cn('text-sm truncate', isSelected ? 'font-medium text-primary' : '')}
                title={brand.name}
              >
                {brand.name}
              </span>
            </label>
          );
        })}
      </div>

      {hasMoreBrands && !showAllBrands && (
        <Button
          variant="link"
          size="custom_sm"
          className="px-2 h-auto text-xs font-normal text-muted-foreground"
          onClick={() => setShowAllBrands(true)}
        >
          {t('shop.filters.brands.show.more')}
        </Button>
      )}
    </div>
  );
};

export { ProductBrandFilters };
